import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtNum, toEnglishDigits } from "@/utils/toEnglishDigits";
import isTrainStation from "@/utils/isTrainStation";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Plus, Trash, MapPin, ArrowLeft, ArrowsDownUp, PencilSimple } from "@phosphor-icons/react";
import CustomDatePicker, { CustomTimePicker } from "@/components/DateTimePicker";

// -------- Time-math utilities (spec 2026-07-22 v3 §1) --------
// Used to auto-fill the new-route time as "last route + 2h" AND to validate
// that the new route is chronologically AFTER the previous one.
function parseHMToMinutes(t) {
  if (!t) return null;
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = Number(m[1]);
  const mn = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(mn) || h < 0 || h > 23 || mn < 0 || mn > 59) return null;
  return h * 60 + mn;
}
function minutesToHM(mins) {
  const wrapped = ((Math.round(mins) % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
function addHoursToTimeString(t, hours) {
  const mins = parseHMToMinutes(t);
  if (mins == null) return "";
  return minutesToHM(mins + hours * 60);
}

/**
 * Admin-only routes editor. Server (PATCH /admin/bookings/:id) recomputes
 * pricing for every route from route_rules — this component never sends prices.
 *
 * Props:
 *   booking      — the current booking object (must include id).
 *   onUpdated(b) — called with the updated booking after any add/remove.
 *   showAddButton (default true) — set to false when the parent renders its
 *                                    own "add route" trigger elsewhere.
 *
 * Ref API:
 *   ref.current.openAdd() — opens the add-route modal from an external button.
 */
const RoutesEditor = forwardRef(function RoutesEditor(
  { booking, onUpdated, showAddButton = true },
  ref,
) {
  const initialRoutes = useMemo(() => {
    if (Array.isArray(booking?.routes) && booking.routes.length) {
      return booking.routes;
    }
    // Derive a single route from the flat booking so the UI never renders empty.
    // 2026-02-24 §PerRouteFlight: also carry over the booking-level flight
    // fields into the derived route so the row shows the correct FZ 873 /
    // airline (legacy single-leg bookings). New per-route additions won't
    // inherit these — they start empty in AddRouteModal.
    return [
      {
        pickup_location: booking?.pickup_location || "",
        destination: booking?.destination || booking?.dropoff_location || "",
        trip_date: booking?.trip_date || "",
        trip_time: booking?.trip_time || "",
        flight_number: booking?.flight_number || null,
        flight_airline: booking?.flight_airline || null,
        cars: [
          {
            car_type: booking?.car_type || "",
            quantity: Number(booking?.quantity || 1) || 1,
          },
        ],
      },
    ];
  }, [booking]);

  const [rows, setRows] = useState(initialRoutes);
  useEffect(() => setRows(initialRoutes), [initialRoutes]);

  const [cars, setCars] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/cars").then((r) => {
      if (cancelled) return;
      setCars(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setCars([]));
    return () => { cancelled = true; };
  }, []);

  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  // 2026-02-26 §EditRoute: `null` = not editing (Add mode), integer =
  // editing the route at that index (Edit mode). Used to prefill the modal
  // and swap the submit-handler between "append" and "replace at index".
  const [editIdx, setEditIdx] = useState(null);
  const [removeIdx, setRemoveIdx] = useState(null);

  // Imperative handle used by Timeline card's "add route" trigger.
  useImperativeHandle(ref, () => ({ openAdd: () => { setEditIdx(null); setShowAdd(true); } }), []);

  // Send an updated routes array to the server. Prices are ignored server-side.
  async function submit(nextRoutes) {
    if (!booking?.id) return;
    setBusy(true);
    try {
      // CRITICAL (spec 2026-07-22 v3.1 §1): send trip_date/trip_time EXPLICITLY
      // per route. Never let them be `undefined`, otherwise the backend used to
      // fall back to `booking.trip_time` and all routes ended up sharing the
      // header time — the root cause of MZ-000587's "both routes at 06:55" bug.
      //
      // Spec v3.2 §Train-Auto-Detect: if EITHER pickup/destination looks like
      // a train station, force flight_airline="قطار" and flight_number=null so
      // stale "FZ 873" values from a previous edit can never survive a save.
      const payload = {
        routes: nextRoutes.map((r) => {
          const _isTrain =
            isTrainStation(r.pickup_location) || isTrainStation(r.destination);
          // 2026-02-26 §PerRouteFinancials: pass commission + cash on the
          // route object. Undefined values (or ≤0) become server-side null
          // so the API can distinguish "unset" from "explicit zero".
          const _comm = Number(r.agent_commission);
          const _cash = Number(r.cash_from_client);
          return {
            pickup_location: r.pickup_location,
            destination: r.destination,
            trip_date: r.trip_date || r.date || null,
            trip_time: r.trip_time || r.time || null,
            flight_number: _isTrain ? null : (r.flight_number || undefined),
            flight_airline: _isTrain ? "قطار" : (r.flight_airline || undefined),
            passengers: r.passengers || undefined,
            agent_commission: !Number.isNaN(_comm) && _comm > 0 ? _comm : undefined,
            cash_from_client: !Number.isNaN(_cash) && _cash > 0 ? _cash : undefined,
            cars: (r.cars || []).map((c) => ({
              car_type: c.car_type,
              quantity: Number(c.quantity) || 1,
            })),
          };
        }),
      };
      const { data } = await api.patch(
        `/admin/bookings/${booking.id}`,
        payload,
      );
      toast.success("تم تحديث المسارات");
      onUpdated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّر تحديث المسارات");
    } finally {
      setBusy(false);
    }
  }

  function handleAdd(newRoute) {
    setShowAdd(false);
    if (editIdx !== null && editIdx >= 0 && editIdx < rows.length) {
      // 2026-02-26 §EditRoute: replace the row at `editIdx` instead of appending.
      submit(rows.map((r, i) => (i === editIdx ? newRoute : r)));
    } else {
      submit([...rows, newRoute]);
    }
    setEditIdx(null);
  }

  function openEdit(i) {
    setEditIdx(i);
    setShowAdd(true);
  }

  function openAdd() {
    setEditIdx(null);
    setShowAdd(true);
  }

  function handleRemove(idx) {
    setRemoveIdx(null);
    submit(rows.filter((_, i) => i !== idx));
  }

  async function handleAutoReorder() {
    if (orderCheck.ordered) return;
    const sortKey = (r) => {
      const d = r.trip_date || r.date || "";
      const t = r.trip_time || r.time || "";
      return `${d}T${t}`;
    };
    // Stable sort with original-index tie-breaker so rows missing (date,time)
    // preserve their relative order.
    const withIdx = rows.map((r, i) => ({ r, i }));
    withIdx.sort((a, b) => {
      const ka = sortKey(a.r);
      const kb = sortKey(b.r);
      if (ka === kb) return a.i - b.i;
      return ka < kb ? -1 : 1;
    });
    await submit(withIdx.map((x) => x.r));
  }

  // Chronological order check (2026-07-22 v3.2 §UX). Compares each route's
  // (date, time) with the previous one. Returns:
  //   { ordered: bool, issues: [{ index, prev, current, msg }] }
  const orderCheck = useMemo(() => {
    const issues = [];
    for (let i = 1; i < rows.length; i++) {
      const prev = rows[i - 1] || {};
      const cur = rows[i] || {};
      const prevD = prev.trip_date || prev.date || "";
      const prevT = prev.trip_time || prev.time || "";
      const curD = cur.trip_date || cur.date || "";
      const curT = cur.trip_time || cur.time || "";
      if (!prevD || !prevT || !curD || !curT) continue; // skip incomplete
      const prevKey = `${prevD}T${prevT}`;
      const curKey = `${curD}T${curT}`;
      if (curKey <= prevKey) {
        issues.push({
          index: i,
          msg: `المسار ${i + 1} (${curD} ${curT}) ليس بعد المسار ${i} (${prevD} ${prevT})`,
        });
      }
    }
    return { ordered: issues.length === 0, issues };
  }, [rows]);

  return (
    <section
      dir="rtl"
      className="mt-6 rounded-2xl border border-primary/20 bg-card p-4"
      data-testid="routes-editor"
    >
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-primary font-bold text-lg">المسارات</h3>
          {rows.length > 1 && (
            orderCheck.ordered ? (
              <span
                data-testid="routes-order-badge-ok"
                title="جميع المسارات مرتّبة تصاعدياً حسب التاريخ والوقت"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#2E7D32]/20 border border-[#2E7D32]/60 text-[#4CAF50]"
              >
                ✓ مرتّبة زمنياً
              </span>
            ) : (
              <>
                <span
                  data-testid="routes-order-badge-warn"
                  title={orderCheck.issues.map((x) => x.msg).join("\n")}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#C62828]/20 border border-[#C62828]/60 text-[#EF5350] animate-pulse"
                >
                  ⚠️ تسلسل غير مرتّب
                </span>
                <button
                  type="button"
                  onClick={handleAutoReorder}
                  disabled={busy}
                  data-testid="routes-auto-reorder-btn"
                  title="فرز المسارات تصاعدياً حسب التاريخ والوقت — يُحفظ فوراً"
                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-primary text-background hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ⇅ إعادة ترتيب تلقائي
                </button>
              </>
            )
          )}
        </div>
        {showAddButton && (
          <button
            type="button"
            onClick={openAdd}
            disabled={busy}
            data-testid="btn-add-route"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-primary/15 border border-primary/50 text-primary hover:bg-primary/25 disabled:opacity-50"
          >
            <Plus size={13} weight="bold" /> إضافة مسار
          </button>
        )}
      </header>

      <p className="text-white/50 text-[11px] mb-3">
        سيتم حساب السعر تلقائياً من قواعد التسعير الحالية عند كل تعديل.
      </p>

      <ol className="space-y-2">
        {rows.map((r, i) => {
          const rowIssue = orderCheck.issues.find((x) => x.index === i);
          return (
          <li
            key={i}
            className={
              "flex items-center gap-2 rounded-xl p-3 " +
              (rowIssue
                ? "bg-[#C62828]/10 border border-[#C62828]/50"
                : "bg-background/40 border border-primary/10")
            }
            data-testid={`route-row-${i}`}
            title={rowIssue ? rowIssue.msg : undefined}
          >
            <span className="text-primary font-bold num min-w-[24px]">
              {fmtNum(i + 1)}.
            </span>
            {rowIssue && (
              <span
                aria-hidden
                data-testid={`route-row-${i}-order-warn`}
                className="text-[#EF5350] text-sm"
                title={rowIssue.msg}
              >
                ⚠️
              </span>
            )}
            <MapPin size={14} className="text-primary" />
            <span className="text-white">{r.pickup_location || "—"}</span>
            <ArrowLeft size={12} className="text-white/40" />
            <span className="text-white">{r.destination || "—"}</span>
            <span className="text-white/45 text-[11px] num" dir="ltr">
              {(r.trip_date || r.date) ? `· ${toEnglishDigits(r.trip_date || r.date)}` : ""}
              {(r.trip_time || r.time) ? ` · ${toEnglishDigits(r.trip_time || r.time)}` : ""}
            </span>
            {(r.cars || []).length > 0 && (
              <span className="text-white/60 text-[11px] mr-auto num">
                {(r.cars || []).map((c) =>
                  `${c.car_type} × ${toEnglishDigits(String(c.quantity || 1))}`,
                ).join(" / ")}
              </span>
            )}
            {/* 2026-02-24 §PerRouteFlight: each route shows ITS OWN flight
                info (or 🚄 قطار badge when the route touches a train
                station). Never falls back to booking-level so an FZ 873
                stamped on route 1 doesn't leak into route 2. */}
            {(isTrainStation(r.pickup_location) || isTrainStation(r.destination)) ? (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#4A90E2]/20 border border-[#4A90E2]/50 text-[#4A90E2]"
                data-testid={`route-row-${i}-train-badge`}
              >
                🚄 قطار
              </span>
            ) : (r.flight_number || r.flight_airline) ? (
              <span
                className="text-white/60 text-[10px] num"
                dir="ltr"
                data-testid={`route-row-${i}-flight`}
              >
                {[r.flight_airline, r.flight_number].filter(Boolean).join(" · ")}
              </span>
            ) : null}
            {Number(r.route_total || 0) > 0 && (
              <span className="text-primary font-bold num text-sm ml-2">
                {fmtNum(r.route_total)} ر.س
              </span>
            )}
            {/* 2026-02-26 §PerRouteFinancials: coloured chips per route.
                Amber = commission (deducted from company profit).
                Blue  = cash the driver collected from client. */}
            {Number(r.agent_commission || 0) > 0 && (
              <span
                data-testid={`route-row-${i}-commission-chip`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#B45309]/20 border border-[#B45309]/60 text-[#F59E0B] num"
                dir="ltr"
                title="عمولة المندوب لهذا المسار"
              >
                عمولة: {fmtNum(Number(r.agent_commission).toFixed(0))}
              </span>
            )}
            {Number(r.cash_from_client || 0) > 0 && (
              <span
                data-testid={`route-row-${i}-cash-chip`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#3B82F6]/20 border border-[#3B82F6]/60 text-[#60A5FA] num"
                dir="ltr"
                title="عميل كاش لهذا المسار"
              >
                كاش: {fmtNum(Number(r.cash_from_client).toFixed(0))}
              </span>
            )}
            {rows.length > 1 && (
              <>
                {/* 2026-02-26 §EditRoute: pencil button opens AddRouteModal
                    prefilled with this route's data. Same modal handles
                    both Add and Edit — behaviour depends on `editIdx`. */}
                <button
                  type="button"
                  onClick={() => openEdit(i)}
                  disabled={busy}
                  data-testid={`btn-edit-route-${i}`}
                  title="تعديل المسار"
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/20 text-primary hover:bg-primary/35 disabled:opacity-40"
                >
                  <PencilSimple size={12} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={() => setRemoveIdx(i)}
                  disabled={busy}
                  data-testid={`btn-remove-route-${i}`}
                  title="حذف المسار"
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#C62828] text-white hover:bg-[#B71C1C] disabled:opacity-40"
                >
                  <Trash size={12} weight="bold" />
                </button>
              </>
            )}
            {rows.length === 1 && (
              <button
                type="button"
                onClick={() => openEdit(i)}
                disabled={busy}
                data-testid={`btn-edit-route-${i}`}
                title="تعديل المسار"
                className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/20 text-primary hover:bg-primary/35 disabled:opacity-40"
              >
                <PencilSimple size={12} weight="bold" />
              </button>
            )}
          </li>
        );
        })}
      </ol>

      {showAdd && (
        <AddRouteModal
          bookingId={booking?.id}
          cars={cars}
          fallbackCarType={
            (editIdx !== null && editIdx >= 0 ? rows[editIdx]?.cars?.[0]?.car_type : null) ||
            rows?.[0]?.cars?.[0]?.car_type ||
            booking?.car_type ||
            ""
          }
          // 2026-02-26 §EditRoute: pass the route being edited for prefill.
          editing={editIdx !== null && editIdx >= 0 ? rows[editIdx] : null}
          editIndex={editIdx}
          // When editing, prefill from the row itself; when adding, use the
          // "return leg" swap heuristic.
          defaultPickup={
            (editIdx !== null && editIdx >= 0 ? rows[editIdx]?.pickup_location : null) ||
            rows?.[rows.length - 1]?.destination ||
            booking?.destination ||
            booking?.dropoff_location || ""
          }
          defaultDestination={
            (editIdx !== null && editIdx >= 0 ? rows[editIdx]?.destination : null) ||
            rows?.[rows.length - 1]?.pickup_location ||
            booking?.pickup_location || ""
          }
          defaultDate={
            (editIdx !== null && editIdx >= 0 ? (rows[editIdx]?.trip_date || rows[editIdx]?.date) : null) ||
            rows?.[rows.length - 1]?.trip_date ||
            rows?.[rows.length - 1]?.date ||
            booking?.trip_date || ""
          }
          // Add mode: last + 2h. Edit mode: keep the row's own time.
          defaultTime={
            editIdx !== null && editIdx >= 0
              ? (rows[editIdx]?.trip_time || rows[editIdx]?.time || "")
              : (addHoursToTimeString(
                  rows?.[rows.length - 1]?.trip_time ||
                  rows?.[rows.length - 1]?.time ||
                  booking?.trip_time || "",
                  2,
                ) || booking?.trip_time || "")
          }
          defaultPassengers={
            (editIdx !== null && editIdx >= 0 ? rows[editIdx]?.passengers : null)
            || booking?.passengers
          }
          lastRoute={rows?.[rows.length - 1] || null}
          fallbackTripDate={booking?.trip_date || ""}
          fallbackTripTime={booking?.trip_time || ""}
          onClose={() => { setShowAdd(false); setEditIdx(null); }}
          onSubmit={handleAdd}
          busy={busy}
        />
      )}

      <AlertDialog
        open={removeIdx !== null}
        onOpenChange={(o) => { if (!o) setRemoveIdx(null); }}
      >
        <AlertDialogContent dir="rtl" className="text-right" data-testid="route-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right">حذف المسار</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              حذف المسار {removeIdx !== null ? fmtNum(removeIdx + 1) : ""}؟ سيُعاد حساب السعر تلقائياً.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>تراجع</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              className="bg-[#C62828] hover:bg-[#B71C1C] text-white"
              onClick={(e) => { e.preventDefault(); handleRemove(removeIdx); }}
              data-testid="confirm-remove-route"
            >
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
});

export default RoutesEditor;

function AddRouteModal({ cars, fallbackCarType, defaultPickup, defaultDestination, defaultDate, defaultTime, defaultPassengers, bookingId, lastRoute, fallbackTripDate, fallbackTripTime, editing, editIndex, onClose, onSubmit, busy }) {
  // 2026-02-26 §EditRoute: `editing` is the route being edited (null in Add
  // mode). Used to prefill flight_number/airline + cars, and to swap the
  // modal title + submit-button label.
  const isEditMode = editing != null && editIndex != null && editIndex >= 0;
  const [pickup, setPickup] = useState(defaultPickup || "");
  const [destination, setDestination] = useState(defaultDestination || "");
  // Flight fields — prefilled from `editing` in Edit mode (per-route values).
  const [flightNumber, setFlightNumber] = useState(
    isEditMode ? (editing?.flight_number || "") : ""
  );
  const [flightAirline, setFlightAirline] = useState(
    isEditMode ? (editing?.flight_airline || "") : ""
  );
  // 2026-02-26 §PerRouteFinancials: per-route commission + cash from client.
  // Empty by default in Add mode. In Edit mode, prefilled from the ROUTE
  // being edited only (never inherited from other routes or booking-level).
  const [routeCommission, setRouteCommission] = useState(
    isEditMode && editing?.agent_commission != null && Number(editing.agent_commission) > 0
      ? String(editing.agent_commission)
      : ""
  );
  const [routeCash, setRouteCash] = useState(
    isEditMode && editing?.cash_from_client != null && Number(editing.cash_from_client) > 0
      ? String(editing.cash_from_client)
      : ""
  );
  // Optional "location details" appended to the city on submit
  // (e.g. hotel name, meeting point). Spec 2026-07 §5.2 —
  // final value = "{city} {detail}" trimmed.
  const [pickupDetail, setPickupDetail] = useState("");
  const [destinationDetail, setDestinationDetail] = useState("");
  // { [car_id]: quantity } — matches the "new booking" screen's UX.
  // In Edit mode: prefill with the route's cars array.
  const [carQty, setCarQty] = useState(() => {
    if (!isEditMode) return {};
    const initial = {};
    for (const c of (editing?.cars || [])) {
      if (c?.car_type) initial[c.car_type] = Number(c.quantity || 1);
    }
    return initial;
  });
  const [date, setDate] = useState(defaultDate || "");
  const [time, setTime] = useState(defaultTime || "");
  const [passengers, setPassengers] = useState(
    defaultPassengers ? String(defaultPassengers) : "",
  );
  const [preview, setPreview] = useState({ total: 0, loading: false, hasRule: true });

  // Combined values sent to the preview API + parent onSubmit.
  const finalPickup = useMemo(
    () => [pickup.trim(), pickupDetail.trim()].filter(Boolean).join(" ").trim(),
    [pickup, pickupDetail],
  );
  const finalDestination = useMemo(
    () => [destination.trim(), destinationDetail.trim()].filter(Boolean).join(" ").trim(),
    [destination, destinationDetail],
  );

  // Live train detection (spec 2026-07-22 v3.2 §Train-Auto-Detect). Detect
  // from the FINAL combined strings so "محطة قطار مكة" in the detail field
  // also flips the switch.
  const isTrain = useMemo(
    () => isTrainStation(finalPickup) || isTrainStation(finalDestination),
    [finalPickup, finalDestination],
  );
  const _prevIsTrain = useRef(isTrain);
  useEffect(() => {
    if (isTrain && !_prevIsTrain.current) {
      // Switched INTO train mode → stamp airline + clear number
      setFlightAirline("قطار");
      setFlightNumber("");
    } else if (!isTrain && _prevIsTrain.current) {
      // Switched OUT of train mode → clear the auto-stamp so admin can type
      setFlightAirline((prev) => (prev === "قطار" ? "" : prev));
    }
    _prevIsTrain.current = isTrain;
  }, [isTrain]);

  // Suggested city chips — spec 2026-07 §5.2. `includeShrines` on destination
  // adds pilgrimage-only options (مزارات / جولة سياحية).
  const PICKUP_CITIES = useMemo(() => [
    "مكة المكرمة",
    "جدة",
    "الطائف",
    "المدينة المنورة",
    "محطة قطار مكة",
    "محطة قطار المدينة",
    "الفندق",
  ], []);
  const DEST_EXTRAS = useMemo(() => [
    "مزارات",
    "جولة سياحية في المدينة المنورة",
  ], []);

  // 🆕 Landmark / keyword → implied city (spec 2026-07-22 v2 §5.4).
  // Even without typing the city name explicitly, the corresponding chip
  // auto-highlights when the admin writes a well-known place. Longest
  // patterns first so "مكة المكرمة" beats "مكة".
  const CITY_MATCHERS = useMemo(
    () => [
      { city: "مكة المكرمة", keywords: [
        "مكة المكرمة", "مكه المكرمه", "مكة", "مكه",
        "الحرم المكي", "المسجد الحرام", "ابراج البيت", "أبراج البيت",
        "برج الساعة", "الفيرمونت", "فيرمونت", "زمزم",
        "العزيزية", "الششة", "الششه", "المشاعر",
      ]},
      { city: "المدينة المنورة", keywords: [
        "المدينة المنورة", "المدينه المنوره", "المدينة", "المدينه",
        "المسجد النبوي", "الحرم النبوي",
        "مطار الأمير محمد بن عبدالعزيز", "مطار محمد بن عبدالعزيز",
        "قباء", "قبا", "قربان", "أحد", "احد",
      ]},
      { city: "جدة", keywords: [
        "جدة", "جده",
        "مطار الملك عبدالعزيز", "الملك عبدالعزيز الدولي", "KAIA", "kaia",
        "مطار جدة", "مطار جده", "الكورنيش", "طريق المدينة", "أبحر", "ابحر",
      ]},
      { city: "الطائف", keywords: [
        "الطائف", "الطايف", "طائف",
        "مطار الطائف", "الهدا", "الهده", "الشفا",
      ]},
      { city: "محطة قطار مكة",  keywords: ["محطة قطار مكة", "قطار مكة", "قطار الحرمين مكة"] },
      { city: "محطة قطار المدينة", keywords: ["محطة قطار المدينة", "قطار المدينة", "قطار الحرمين المدينة"] },
      { city: "الفندق", keywords: ["الفندق"] },
    ],
    [],
  );

  // Normalize Arabic text for tolerant substring matching:
  //   ا/إ/أ/آ → ا · ى → ي · ة → ه · strip diacritics + tatweel
  const _norm = (s) =>
    String(s || "")
      .replace(/[\u064B-\u0652\u0670\u0640]/g, "")
      .replace(/[إأآا]/g, "ا")
      .replace(/ى/g, "ي")
      .replace(/ة/g, "ه")
      .toLowerCase()
      .trim();

  const detectCity = useCallback(
    (text) => {
      const tn = _norm(text);
      if (!tn) return null;
      for (const m of CITY_MATCHERS) {
        for (const kw of m.keywords) {
          const kwn = _norm(kw);
          if (kwn && tn.indexOf(kwn) !== -1) return m.city;
        }
      }
      return null;
    },
    [CITY_MATCHERS],
  );
  const pickupDetectedCity = useMemo(
    () => detectCity(`${pickup} ${pickupDetail}`),
    [pickup, pickupDetail, detectCity],
  );
  const destinationDetectedCity = useMemo(
    () => detectCity(`${destination} ${destinationDetail}`),
    [destination, destinationDetail, detectCity],
  );

  // 2026-02-26 §HotelHint: when ONE end is "الفندق" and the OTHER end is a
  // known main city (مكة/المدينة/جدة/الطائف), suggest a "فندق {الطرف الآخر}"
  // hint chip so admins can auto-fill the city context of the hotel without
  // typing. Clicking it replaces the input with the CITY name — the input
  // stays open so the admin can append the specific hotel name.
  //
  // 2026-02-27 §HotelHintTrainStations: a train station on the OTHER side
  // also triggers the hint, mapped to its host city — "محطة قطار مكة" →
  // "فندق مكة المكرمة", "محطة قطار المدينة" → "فندق المدينة المنورة".
  const HOTEL_CITY_MAP = useMemo(() => ({
    "مكة المكرمة": "مكة المكرمة",
    "المدينة المنورة": "المدينة المنورة",
    "جدة": "جدة",
    "الطائف": "الطائف",
    "محطة قطار مكة المكرمة": "مكة المكرمة",
    "محطة قطار المدينة المنورة": "المدينة المنورة",
  }), []);
  const _mapToHostCity = useCallback((raw) => {
    if (!raw) return null;
    const key = String(raw).trim();
    return HOTEL_CITY_MAP[key] || null;
  }, [HOTEL_CITY_MAP]);
  const pickupHotelHint = useMemo(() => {
    if (pickup !== "الفندق" && pickupDetectedCity !== "الفندق") return null;
    const mapped = _mapToHostCity(destination);
    if (mapped) return mapped;
    // 2026-02-26 §HotelHintGuard: only trust the fuzzy detected city when
    // the raw input is EMPTY. Otherwise a random detail like "شارع مكة"
    // fuzzy-matches "مكة" (substring) and the hint chip would wrongly appear.
    if (!destination.trim() && destinationDetectedCity) {
      return _mapToHostCity(destinationDetectedCity);
    }
    return null;
  }, [pickup, pickupDetectedCity, destination, destinationDetectedCity, _mapToHostCity]);
  const destinationHotelHint = useMemo(() => {
    if (destination !== "الفندق" && destinationDetectedCity !== "الفندق") return null;
    const mapped = _mapToHostCity(pickup);
    if (mapped) return mapped;
    if (!pickup.trim() && pickupDetectedCity) {
      return _mapToHostCity(pickupDetectedCity);
    }
    return null;
  }, [destination, destinationDetectedCity, pickup, pickupDetectedCity, _mapToHostCity]);

  const inc = (id) =>
    setCarQty((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  const dec = (id) =>
    setCarQty((prev) => {
      const cur = prev[id] || 0;
      if (cur <= 0) return prev;
      const next = { ...prev };
      if (cur - 1 === 0) delete next[id];
      else next[id] = cur - 1;
      return next;
    });

  // Dynamic passenger cap (spec 2026-07-22 addendum §1-2): sum of
  // `capacity × quantity` across all selected cars. When zero, the
  // passengers input is disabled.
  const maxCapacity = useMemo(() => {
    let total = 0;
    for (const c of cars) {
      const id = c.id || c.car_type;
      const q = Number(carQty[id] || 0);
      const cap = Number(c.capacity ?? c.max_passengers ?? 0);
      if (q > 0 && cap > 0) total += q * cap;
    }
    return total;
  }, [carQty, cars]);

  // Auto-clamp when the cap DROPS (user reduced cars).
  useEffect(() => {
    if (!passengers) return;
    const n = Number(passengers);
    if (maxCapacity > 0 && n > maxCapacity) {
      setPassengers(String(maxCapacity));
    } else if (maxCapacity === 0 && passengers) {
      // no cars selected → clear the field so placeholder shows again
      setPassengers("");
    }
  }, [maxCapacity, passengers]);

  const handlePaxChange = (raw) => {
    const digitsOnly = String(raw).replace(/[^0-9]/g, "");
    if (!digitsOnly) return setPassengers("");
    const n = Number(digitsOnly);
    if (maxCapacity > 0 && n > maxCapacity) {
      setPassengers(String(maxCapacity));
      toast.error(`الحد الأقصى ${fmtNum(maxCapacity)} حسب السيارات المختارة`);
    } else {
      setPassengers(digitsOnly);
    }
  };

  // Live price preview (debounced 300ms). Skips API when required inputs are
  // incomplete so the endpoint isn't spammed while the modal opens.
  const selectedCars = useMemo(
    () =>
      Object.entries(carQty)
        .filter(([, q]) => Number(q) > 0)
        .map(([car_type, q]) => ({ car_type, quantity: Number(q) })),
    [carQty],
  );
  const carsKey = JSON.stringify(selectedCars);
  useEffect(() => {
    if (!bookingId) return;
    const p = finalPickup;
    const d = finalDestination;
    if (!p || !d || selectedCars.length === 0) {
      setPreview({ total: 0, loading: false, hasRule: true });
      return;
    }
    let cancelled = false;
    setPreview((prev) => ({ ...prev, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(
          `/admin/bookings/${bookingId}/preview-route-price`,
          {
            pickup_location: p,
            destination: d,
            cars: selectedCars,
            trip_date: date || undefined,
            trip_time: time || undefined,
            passengers: passengers ? Number(passengers) : undefined,
          },
        );
        if (cancelled) return;
        setPreview({
          total: Number(data?.route_total) || 0,
          loading: false,
          hasRule: !!data?.has_pricing_rule,
        });
      } catch {
        if (!cancelled) setPreview({ total: 0, loading: false, hasRule: true });
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, finalPickup, finalDestination, carsKey, date, time, passengers]);

  function submit() {
    const p = finalPickup;
    const d = finalDestination;
    if (!p) { toast.error("نقطة الانطلاق مطلوبة"); return; }
    if (!d) { toast.error("الوجهة مطلوبة"); return; }
    if (selectedCars.length === 0) {
      toast.error("اختر سيارة واحدة على الأقل");
      return;
    }
    // 2026-02-24 §FreeTime: chronological "new route must be after last"
    // validation removed per admin request. The auto-suggested time (last
    // route + 2h) is kept as a HINT only — admin can pick any date/time.
    onSubmit({
      pickup_location: p,
      destination: d,
      trip_date: date || undefined,
      trip_time: time || undefined,
      // Flight (spec v3.2 §Train). Send explicit values so the outer
      // RoutesEditor payload never falls back to undefined for train routes.
      flight_number: isTrain ? null : (flightNumber.trim() || undefined),
      flight_airline: isTrain ? "قطار" : (flightAirline.trim() || undefined),
      passengers: passengers ? Number(passengers) : undefined,
      // 2026-02-26 §PerRouteFinancials: per-route commission + cash. Only
      // send when > 0 so the server treats the field as "unset" otherwise.
      agent_commission:
        routeCommission && Number(routeCommission) > 0
          ? Number(routeCommission)
          : undefined,
      cash_from_client:
        routeCash && Number(routeCash) > 0 ? Number(routeCash) : undefined,
      cars: selectedCars,
    });
  }

  return (
    <div
      dir="rtl"
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="add-route-modal"
    >
      <div
        className="bg-card border border-primary/30 rounded-2xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-primary font-bold text-lg" data-testid="add-route-modal-title">
            {isEditMode ? `تعديل المسار ${fmtNum(editIndex + 1)}` : "إضافة مسار جديد"}
          </h4>
          <button
            type="button"
            onClick={onClose}
            aria-label="إغلاق"
            className="text-white/60 hover:text-white text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <label className="block space-y-1">
          <span className="text-white/70 text-xs">من (نقطة الانطلاق)</span>
          <input
            type="text"
            value={pickup}
            onChange={(e) => setPickup(e.target.value)}
            className="mz-input w-full"
            data-testid="add-route-pickup"
          />
          <div className="flex flex-wrap gap-1.5 pt-1" data-testid="pickup-city-chips">
            {PICKUP_CITIES.map((c) => {
              const isTyped = pickup === c;
              const isDetected = !isTyped && pickupDetectedCity === c;
              const active = isTyped || isDetected;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setPickup(c)}
                  data-testid={`pickup-chip-${c}`}
                  data-detected={isDetected ? "true" : undefined}
                  className={
                    "px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors " +
                    (active
                      ? (isDetected
                          ? "bg-primary/30 text-primary border-primary/70 ring-1 ring-primary/40"
                          : "bg-primary text-background border-primary")
                      : "bg-transparent text-white/70 border-white/20 hover:bg-white/5")
                  }
                >
                  {c}
                </button>
              );
            })}
          </div>
          {/* 2026-02-26 §HotelHint: shown when pickup=الفندق AND destination
              is a known main city → click fills the pickup with the CITY
              name (not "فندق {city}") so the admin can then append the
              specific hotel name in the detail input right below. */}
          {pickupHotelHint && (
            <div className="pt-1.5" data-testid="pickup-hotel-hint-wrap">
              <button
                type="button"
                onClick={() => setPickup(pickupHotelHint)}
                data-testid="pickup-hotel-hint-chip"
                className={
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-bold border transition-colors " +
                  (pickup.trim().startsWith(pickupHotelHint)
                    ? "bg-primary text-background border-primary"
                    : "bg-primary/10 text-primary border-primary/60 border-dashed hover:bg-primary/20")
                }
                title="اضغط لتعبئة المدينة، ثم أكمل اسم الفندق في حقل التفاصيل"
              >
                🛏️ فندق {pickupHotelHint}
              </button>
              <p className="text-[10.5px] text-white/50 mt-1">
                اضغط لملء المدينة، ثم أكمل اسم الفندق في حقل التفاصيل.
              </p>
            </div>
          )}
          <input
            type="text"
            value={pickupDetail}
            onChange={(e) => setPickupDetail(e.target.value)}
            placeholder="تفاصيل الموقع (اختياري) — اسم فندق، نقطة تجمّع…"
            className="mz-input w-full mt-1"
            data-testid="add-route-pickup-detail"
          />
        </label>

        <div className="flex justify-center -my-1">
          <button
            type="button"
            onClick={() => {
              // Spec 2026-07-24 v6 §Swap-Fix: also swap the landmark detail
              // fields so "الطائف المطار" ↔ "مكة المكرمة فندق كونراد" flips
              // cleanly, not just the leading city chip. Previously only
              // pickup ↔ destination were swapped, leaving landmark strings
              // stuck to the wrong side.
              const prevPickup = pickup;
              const prevPickupDetail = pickupDetail;
              setPickup(destination);
              setDestination(prevPickup);
              setPickupDetail(destinationDetail);
              setDestinationDetail(prevPickupDetail);
            }}
            data-testid="add-route-swap"
            title="عكس من ↔ إلى"
            aria-label="عكس من وإلى"
            style={{ width: 44, height: 44 }}
            className="inline-flex items-center justify-center rounded-full border border-primary/60 text-primary bg-background hover:bg-primary hover:text-background transition-colors shadow-sm"
          >
            <ArrowsDownUp size={19} weight="bold" />
          </button>
        </div>

        <label className="block space-y-1">
          <span className="text-white/70 text-xs">إلى (الوجهة)</span>
          <input
            type="text"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            className="mz-input w-full"
            data-testid="add-route-destination"
          />
          <div className="flex flex-wrap gap-1.5 pt-1" data-testid="destination-city-chips">
            {[...PICKUP_CITIES, ...DEST_EXTRAS].map((c) => {
              const isTyped = destination === c;
              const isDetected = !isTyped && destinationDetectedCity === c;
              const active = isTyped || isDetected;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setDestination(c)}
                  data-testid={`destination-chip-${c}`}
                  data-detected={isDetected ? "true" : undefined}
                  className={
                    "px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors " +
                    (active
                      ? (isDetected
                          ? "bg-primary/30 text-primary border-primary/70 ring-1 ring-primary/40"
                          : "bg-primary text-background border-primary")
                      : "bg-transparent text-white/70 border-white/20 hover:bg-white/5")
                  }
                >
                  {c}
                </button>
              );
            })}
          </div>
          {/* 2026-02-26 §HotelHint: mirror of the pickup hint, triggered when
              destination=الفندق AND pickup is a known main city. Click fills
              the destination with the CITY name so the admin can append the
              specific hotel name in the detail input right below. */}
          {destinationHotelHint && (
            <div className="pt-1.5" data-testid="destination-hotel-hint-wrap">
              <button
                type="button"
                onClick={() => setDestination(destinationHotelHint)}
                data-testid="destination-hotel-hint-chip"
                className={
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-bold border transition-colors " +
                  (destination.trim().startsWith(destinationHotelHint)
                    ? "bg-primary text-background border-primary"
                    : "bg-primary/10 text-primary border-primary/60 border-dashed hover:bg-primary/20")
                }
                title="اضغط لتعبئة المدينة، ثم أكمل اسم الفندق في حقل التفاصيل"
              >
                🛏️ فندق {destinationHotelHint}
              </button>
              <p className="text-[10.5px] text-white/50 mt-1">
                اضغط لملء المدينة، ثم أكمل اسم الفندق في حقل التفاصيل.
              </p>
            </div>
          )}
          <input
            type="text"
            value={destinationDetail}
            onChange={(e) => setDestinationDetail(e.target.value)}
            placeholder="تفاصيل الموقع (اختياري) — اسم فندق، نقطة تجمّع…"
            className="mz-input w-full mt-1"
            data-testid="add-route-destination-detail"
          />
        </label>

        {/* Combined preview so the admin always sees the final string that
            will be saved (city + detail). Helps catch typos before submit. */}
        {(pickupDetail.trim() || destinationDetail.trim()) && (
          <div
            data-testid="add-route-final-preview"
            className="rounded-lg bg-primary/5 border border-primary/20 px-3 py-2 text-[11px] text-white/70 space-y-0.5"
          >
            <div>
              <span className="text-white/50">من:</span>{" "}
              <span className="text-primary font-semibold">{finalPickup || "—"}</span>
            </div>
            <div>
              <span className="text-white/50">إلى:</span>{" "}
              <span className="text-primary font-semibold">{finalDestination || "—"}</span>
            </div>
          </div>
        )}

        {/* Car catalog list with +/- steppers per car type. */}
        <div className="space-y-1">
          <span className="text-white/70 text-xs">السيارات والكميات</span>
          <div
            className="rounded-xl border border-primary/20 divide-y divide-white/5 overflow-hidden"
            data-testid="add-route-cars-list"
          >
            {cars.length === 0 && (
              <div className="p-3 text-center text-white/40 text-sm">جارٍ تحميل السيارات…</div>
            )}
            {cars.map((c) => {
              const id = c.id || c.car_type;
              const qty = carQty[id] || 0;
              const active = qty > 0;
              const cap = Number(c.capacity ?? c.max_passengers ?? 0);
              return (
                <div
                  key={id}
                  data-testid={`car-row-${id}`}
                  className={
                    "flex items-center gap-3 px-3 py-2 transition-colors " +
                    (active ? "bg-primary/15" : "bg-transparent")
                  }
                >
                  <span
                    className={
                      "text-lg " + (active ? "text-primary" : "text-white/45")
                    }
                    aria-hidden
                  >
                    🚗
                  </span>
                  <div className="flex-1 min-w-0">
                    <div
                      className={
                        "text-sm truncate " +
                        (active ? "text-primary font-bold" : "text-white/80")
                      }
                    >
                      {c.name_ar || c.name_en || id}
                    </div>
                    {cap > 0 && (
                      <div
                        data-testid={`car-capacity-${id}`}
                        className="text-[11px] text-white/50 mt-0.5"
                      >
                        السعة: <span className="num">{fmtNum(cap)}</span> ركاب
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => dec(id)}
                      disabled={qty === 0}
                      data-testid={`car-dec-${id}`}
                      aria-label={`إنقاص ${c.name_ar || id}`}
                      className={
                        "inline-flex items-center justify-center w-11 h-11 rounded-full text-white font-bold text-lg " +
                        (qty === 0
                          ? "bg-white/10 cursor-not-allowed opacity-50"
                          : "bg-[#B8963F] hover:bg-[#A5842F]")
                      }
                    >
                      −
                    </button>
                    <span
                      className={
                        "min-w-[24px] text-center num font-extrabold " +
                        (active ? "text-primary" : "text-white/50")
                      }
                      data-testid={`car-qty-${id}`}
                    >
                      {fmtNum(qty)}
                    </span>
                    <button
                      type="button"
                      onClick={() => inc(id)}
                      data-testid={`car-inc-${id}`}
                      aria-label={`زيادة ${c.name_ar || id}`}
                      className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary hover:brightness-110 text-background font-bold text-lg"
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="space-y-1">
            <span className="text-white/70 text-xs">رقم الرحلة (اختياري)</span>
            <input
              type="text"
              value={isTrain ? "" : flightNumber}
              onChange={(e) => setFlightNumber(e.target.value)}
              placeholder={isTrain ? "—" : "SV1052"}
              dir="ltr"
              disabled={isTrain}
              className={
                "mz-input w-full " +
                (isTrain ? "opacity-50 cursor-not-allowed bg-white/5" : "")
              }
              data-testid="add-route-flight-number"
            />
          </label>
          <label className="space-y-1">
            <span className="text-white/70 text-xs">شركة الطيران (اختياري)</span>
            <input
              type="text"
              value={isTrain ? "قطار" : flightAirline}
              onChange={(e) => setFlightAirline(e.target.value)}
              placeholder="الخطوط السعودية"
              disabled={isTrain}
              className={
                "mz-input w-full " +
                (isTrain ? "opacity-50 cursor-not-allowed bg-white/5" : "")
              }
              data-testid="add-route-flight-airline"
            />
          </label>
        </div>
        {isTrain && (
          <div
            data-testid="add-route-train-notice"
            className="rounded-lg bg-primary/10 border border-primary/40 text-primary text-[12px] font-semibold px-3 py-2"
          >
            🚄 حقول الطيران معطّلة تلقائياً — الرحلة عبر القطار
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="space-y-1">
            <span className="text-white/70 text-xs">التاريخ (اختياري)</span>
            <CustomDatePicker
              value={date}
              onChange={(v) => setDate(v || "")}
              placeholder="YYYY-MM-DD"
              testId="add-route-date"
            />
          </label>
          <label className="space-y-1">
            <span className="text-white/70 text-xs">الوقت (اختياري)</span>
            <CustomTimePicker
              value={time}
              onChange={(v) => setTime(v || "")}
              placeholder="HH:MM"
              testId="add-route-time"
              intervals={5}
            />
          </label>
        </div>

        <label className="block space-y-1">
          <span className="text-white/70 text-xs flex items-center gap-2 flex-wrap">
            <span>عدد الركاب</span>
            {maxCapacity > 0 ? (
              <span className="text-primary text-[11px] font-semibold" data-testid="passengers-cap-label">
                (الحد الأقصى: <span className="num" dir="ltr">{fmtNum(maxCapacity)}</span>)
              </span>
            ) : (
              <span className="text-white/40 text-[11px]">
                (اختر السيارات أولاً)
              </span>
            )}
          </span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={passengers}
            onChange={(e) => handlePaxChange(e.target.value)}
            placeholder={
              maxCapacity > 0
                ? `مثال: ${fmtNum(maxCapacity)}`
                : "اختر السيارات أولاً"
            }
            disabled={maxCapacity === 0}
            className={
              "mz-input w-full num " +
              (maxCapacity === 0 ? "opacity-50 cursor-not-allowed" : "")
            }
            dir="ltr"
            data-testid="add-route-passengers"
          />
        </label>

        {/* 2026-02-26 §PerRouteFinancials: per-route commission + cash.
            Empty by default in Add mode. Values do NOT auto-copy between
            routes — each route is independent. */}
        <div className="space-y-2 rounded-xl border border-primary/25 bg-primary/5 p-3">
          <div
            className="text-primary text-xs font-bold"
            data-testid="route-financials-header"
          >
            الحسابات الخاصة بهذا المسار
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-white/70 text-xs">عمولة المندوب</span>
              <input
                type="text"
                inputMode="decimal"
                value={routeCommission}
                onChange={(e) => {
                  const v = String(e.target.value).replace(/[^0-9.]/g, "");
                  setRouteCommission(v);
                }}
                placeholder="0"
                dir="ltr"
                className="mz-input w-full num"
                data-testid="add-route-agent-commission"
              />
            </label>
            <label className="space-y-1">
              <span className="text-white/70 text-xs">عميل كاش</span>
              <input
                type="text"
                inputMode="decimal"
                value={routeCash}
                onChange={(e) => {
                  const v = String(e.target.value).replace(/[^0-9.]/g, "");
                  setRouteCash(v);
                }}
                placeholder="0"
                dir="ltr"
                className="mz-input w-full num"
                data-testid="add-route-cash-from-client"
              />
            </label>
          </div>
          <p className="text-white/50 text-[10.5px]">
            قيم خاصة بهذا المسار فقط، لا تُنقل تلقائياً لباقي المسارات.
          </p>
        </div>

        {/* Live price preview card. */}
        <div
          data-testid="add-route-price-preview"
          className={
            "rounded-xl p-3 border text-right " +
            (preview.total > 0 && preview.hasRule
              ? "bg-primary/10 border-primary/40"
              : "bg-white/5 border-white/10")
          }
        >
          <div className="flex items-center justify-between">
            <span className="text-white/70 text-xs">إجمالي المسار (معاينة)</span>
            {preview.loading ? (
              <span className="text-white/50 text-xs">جارٍ الحساب…</span>
            ) : (
              <span className="text-primary text-lg font-extrabold num" dir="ltr">
                {fmtNum(preview.total || 0)} ر.س
              </span>
            )}
          </div>
          {!preview.loading && !preview.hasRule && selectedCars.length > 0 && (
            <div
              data-testid="no-pricing-rule-warning"
              className="mt-2 rounded-md bg-[#C62828]/15 border border-[#C62828]/50 text-[#FCA5A5] text-[11px] px-2.5 py-1.5"
            >
              ⚠️ لا توجد قاعدة تسعير لهذا المسار — سيتم الحفظ بسعر ٠ ما لم تُضف قاعدة أولاً.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="add-route-cancel"
            className="px-4 py-2 rounded-full text-sm border border-white/20 text-white/70 hover:bg-white/5"
          >
            إلغاء
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="add-route-submit"
            className="px-4 py-2 rounded-full text-sm font-bold bg-primary text-background hover:brightness-110 disabled:opacity-50"
          >
            {busy
              ? (isEditMode ? "جارٍ الحفظ…" : "جارٍ الإضافة…")
              : (isEditMode ? "حفظ التعديلات" : "إضافة المسار")}
          </button>
        </div>
      </div>
    </div>
  );
}
